/**
 * lexicalHtml — Lexical editorState → 조판 HTML **한 벌**.
 *
 * 같은 변환기가 이 폴더에 **글자 하나 안 틀리고 두 벌** 있었다(PaperComposeModal · ProblemPreviewV2).
 * 문항 편집 목록이 세 번째로 같은 것을 필요로 하면서 뽑았다 — 세 벌이 되면 한 곳만 고쳐도
 * 빌드는 통과하고 화면도 떠서, 같은 문제가 자리에 따라 다른 조판으로 그려진다.
 *
 * ── 왜 동적 import 인가 ──────────────────────────────────────────────────
 * 변환에는 에디터 노드 전체(PlaygroundNodes)가 필요하다. 정적으로 들이면 시험지 화면이 편집기를
 * 통째로 짊어진다. 그래서 **본문이 처음 필요할 때 한 번만** 받아 두고 재사용한다.
 * 모듈 스코프에 캐시하므로 이 파일을 함께 쓰는 화면들이 그 한 번을 나눠 쓴다.
 *
 * ── KaTeX 스타일 ─────────────────────────────────────────────────────────
 * 이 파일은 CSS 를 들이지 않는다. `katex.min.css` 는 이미 메인 번들에 있다 —
 * App.js 가 정적으로 물고 있는 화면들(MathConcept · ProblemEdit · MathceptsTemp ·
 * SchoolExamManagement · printPreview 등)이 각자 import 하고, 그 규칙은 전역이다.
 * 여기서 또 import 하면 같은 CSS 가 청크에 한 벌 더 들어간다.
 */

/**
 * Lexical 문서 → HTML 변환 함수를 주는 약속. 처음 부를 때만 모듈을 받아 온다.
 *
 * 돌려주는 함수는 **인자를 제자리에서 고친다**(walk 가 doc.root 를 직접 갈아 끼운다).
 * 같은 객체를 두 번 넣으면 수식이 중첩 변환되므로, 부르는 쪽은 응답에서 갓 만든 객체를 넘기고
 * 결과 HTML 문자열만 들고 있어야 한다.
 */
let lexicalToHtml = null;
export function loadLexicalToHtml() {
  if (!lexicalToHtml) {
    lexicalToHtml = Promise.all([
      import('lexical'),
      import('@lexical/html'),
      import('../../editor/nodes/PlaygroundNodes'),
      import('../utils/latexParser'),
    ]).then(([lexical, lexicalHtml, nodes, latex]) => {
      const walk = (node) => {
        if (!node || typeof node !== 'object') return node; // children 에 null 이 섞여도 크래시하지 않게
        if (node.type === 'equation') node.equation = latex.processMathEquation(node.equation);
        else if (node.type === 'box-content') node.className = 'PlaygroundEditorTheme__boxContent';
        if (Array.isArray(node.children)) node.children = node.children.map(walk);
        return node;
      };
      return (doc) => {
        doc.root = walk(doc.root);
        const editor = lexical.createEditor({ nodes: [...nodes.default] });
        editor.setEditorState(editor.parseEditorState(doc));
        let html = '';
        editor.update(() => { html = lexicalHtml.$generateHtmlFromNodes(editor); });
        return html;
      };
    }).catch((e) => {
      lexicalToHtml = null; // 실패한 약속을 캐시하면 새로고침 전까지 영영 못 받는다
      throw e;
    });
  }
  return lexicalToHtml;
}

/**
 * body_lexical 흡수 — v2 는 **문자열(JSON)**로 주지만 객체로 오는 응답도 받는다.
 * 못 읽으면 null(던지지 않는다) — 그 카드는 평문 요약으로 남는다.
 */
export function parseDoc(v) {
  if (v == null) return null;
  if (typeof v === 'object') return v;
  try { return JSON.parse(v); } catch (_) { return null; }
}

/**
 * editorState 지도 → HTML 지도 `{ [String(problem_id)]: html }`.
 *
 * 규약 셋을 여기서 지킨다 — 부르는 쪽마다 다시 적으면 한 곳이 빠진다:
 *  ① 키를 **문자열로 굳힌다**(응답은 problem_id 를 정수로 준다).
 *  ② 한 문항의 변환 실패가 같은 묶음을 삼키지 않게 **문항별로 가둔다**.
 *  ③ 결과가 빈 문자열이면 **키를 넣지 않는다** — 넣으면 `body != null` 이 되어 평문 폴백이
 *     꺼지고 카드가 통째로 빈칸이 된다.
 *
 * 본문이 하나도 없으면 변환기를 받아 오지 않는다(에디터 노드 전체라 무겁다).
 */
export async function toBodyHtmlMap(docs) {
  const entries = Object.entries(docs || {});
  if (entries.length === 0) return {};
  const toHtml = await loadLexicalToHtml();
  const out = {};
  entries.forEach(([key, raw]) => {
    try {
      const doc = parseDoc(raw);
      // 여기 오는 raw 는 null 이 아니다(없는 본문은 애초에 키가 안 들어온다) — 그래서
      // 못 읽었다면 진짜 이상이다. 조용히 넘기면 그 카드만 평문으로 남는 이유를 알 수 없다.
      if (!doc) { console.error('문제 본문 변환 실패: 읽을 수 없는 editorState', key); return; }
      const html = toHtml(doc);
      if (html) out[String(key)] = html;
    } catch (e) {
      console.error('문제 본문 변환 실패:', key, e);
    }
  });
  return out;
}
